import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faPeriod
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useEffect, useState } from 'react'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import { normalizeSearchString } from '~/utils/sanitize'
import type { DropdownOption as DropdownOptionType } from './Dropdown'
import {
  checkmark,
  optionItem,
  optionItemActive,
  optionItemChevron,
  optionItemChildren,
  optionItemGroup,
  optionItemLabel,
  optionItemNotSelectable,
  optionsList,
  optionsWrapper
} from './Dropdown.css'

type DropdownOptionProps = {
  option: DropdownOptionType
  selectedValues: string[]
  multiple: boolean
  asMenu: boolean
  isUpdating: boolean
  disabled: boolean | string
  onSelection: (option: DropdownOptionType) => void
  childrenAsSubmenu?: boolean
  depth?: number
  parentSelected?: boolean
  searchTerm?: string
  searchTermShouldFilter?: boolean
  isActive?: boolean
}

const matchSearchTerm = (
  option: DropdownOptionType,
  searchTerm: string
): boolean => {
  return normalizeSearchString(option.label).includes(
    normalizeSearchString(searchTerm)
  )
}

const hasMatchingDescendants = (
  option: DropdownOptionType,
  searchTerm: string
): boolean => {
  if (matchSearchTerm(option, searchTerm) && searchTerm.trim() !== '') {
    return true
  }

  if (option.children) {
    return option.children.some((child) =>
      hasMatchingDescendants(child, searchTerm)
    )
  }

  return false
}

export const DropdownOption = ({
  option,
  selectedValues,
  multiple,
  asMenu,
  isUpdating,
  disabled,
  onSelection,
  childrenAsSubmenu = false,
  depth = 0,
  parentSelected = false,
  searchTerm = '',
  searchTermShouldFilter = true,
  isActive = false
}: DropdownOptionProps): ReactElement | null => {
  const hasChildren = option.children && option.children.length > 0
  const isSelected = selectedValues.includes(option.value)
  const isChildSelected =
    hasChildren &&
    option.children?.some((child) =>
      isChildOrDescendantSelected(child, selectedValues)
    )
  const [isExpanded, setIsExpanded] = useState(
    searchTermShouldFilter
      ? isChildSelected || hasMatchingDescendants(option, searchTerm)
      : false
  )

  const isBlocked = isUpdating || disabled !== false
  const [isSubOpen, setIsSubOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    placement: 'right-start',
    open: isSubOpen,
    onOpenChange: setIsSubOpen,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed'
  })

  const hover = useHover(context, {
    move: false,
    delay: { open: 0, close: 150 },
    enabled: !!(childrenAsSubmenu && hasChildren && !isBlocked)
  })
  const submenuDismiss = useDismiss(context, { bubbles: false })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    submenuDismiss
  ])
  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  useEffect(() => {
    if (
      searchTermShouldFilter &&
      !isExpanded &&
      hasMatchingDescendants(option, searchTerm)
    ) {
      setIsExpanded(true)
    }
  }, [searchTerm, option, isExpanded, searchTermShouldFilter])

  if (option.group) {
    return (
      <div className={optionItemGroup}>{option.element ?? option.label}</div>
    )
  }

  const handleClick = () => {
    if (isBlocked) return

    if (childrenAsSubmenu && hasChildren) {
      // Submenu is handled by hover; click does nothing
      return
    }

    if (hasChildren) {
      onSelection(option)
      // When parent is selected, collapse children
      setIsExpanded(false)
    } else {
      onSelection(option)
    }
  }

  const handleChevronClick = (e: React.MouseEvent) => {
    if (childrenAsSubmenu && hasChildren) return
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  if (
    searchTermShouldFilter &&
    !matchSearchTerm(option, searchTerm) &&
    !hasMatchingDescendants(option, searchTerm)
  ) {
    return null
  }

  return (
    <>
      <div
        ref={childrenAsSubmenu && hasChildren ? refs.setReference : undefined}
        className={clsx(
          optionItem,
          !multiple && isSelected ? optionItemNotSelectable : null,
          isActive ? optionItemActive : null
        )}
        {...(childrenAsSubmenu && hasChildren
          ? getReferenceProps({ onClick: handleClick })
          : { onClick: handleClick })}
      >
        <div className={optionItemLabel}>
          {hasChildren && !childrenAsSubmenu && (
            <FontAwesomeIcon
              icon={faChevronDown}
              className={clsx(
                optionItemChevron,
                isExpanded ? 'expanded' : null
              )}
              onClick={handleChevronClick}
            />
          )}
          {option.element ?? option.label}
        </div>

        {hasChildren && childrenAsSubmenu && (
          <FontAwesomeIcon
            icon={faChevronRight}
            className={optionItemChevron}
          />
        )}

        {(isSelected || parentSelected) && !asMenu && (
          <span className={checkmark}>
            <FontAwesomeIcon icon={faCheck} />
          </span>
        )}
        {isChildSelected && (
          <span className={checkmark}>
            <FontAwesomeIcon icon={faPeriod} />
          </span>
        )}
      </div>

      {childrenAsSubmenu && hasChildren && (
        <FloatingPortal>
          {isMounted && (
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex: 10000 }}
              {...getFloatingProps()}
            >
              <div
                className={clsx(themeClass, optionsWrapper)}
                style={transitionStyles}
              >
                <div className={optionsList}>
                  {option.children?.map((child) => (
                    <div
                      key={child.value}
                      className={optionItem}
                      onClick={() => {
                        if (isBlocked) return
                        onSelection(child)
                      }}
                    >
                      {child.element ?? child.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </FloatingPortal>
      )}

      {!childrenAsSubmenu && hasChildren && isExpanded && (
        <div className={optionItemChildren}>
          {option.children?.map((childOption) => (
            <DropdownOption
              key={childOption.value}
              option={childOption}
              selectedValues={selectedValues}
              multiple={multiple}
              asMenu={asMenu}
              isUpdating={isUpdating}
              disabled={disabled}
              onSelection={onSelection}
              childrenAsSubmenu={childrenAsSubmenu}
              depth={depth + 1}
              parentSelected={isSelected || parentSelected}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </>
  )
}

// Helper function to check if a child or any descendant is selected
function isChildOrDescendantSelected(
  option: DropdownOptionType,
  selectedValues: string[]
): boolean {
  if (selectedValues.includes(option.value)) {
    return true
  }

  if (option.children) {
    return option.children.some((child) =>
      isChildOrDescendantSelected(child, selectedValues)
    )
  }

  return false
}
